import { AppRoutes } from '@/constants';

export interface CurrentLineInfo {
  lineStart: number;
  lineEnd: number;
  lineText: string;
  isCursorAtLineStart: boolean;
  isCursorAtLineEnd: boolean;
}

export interface BulletTransformResult {
  nextValue: string;
  nextCursorPos: number;
}

export const getCurrentLineInfo = (value: string, cursor: number): CurrentLineInfo => {
  const lineStart = value.lastIndexOf('\n', cursor - 1) + 1;
  const nextNewlineIndex = value.indexOf('\n', cursor);
  const lineEnd = nextNewlineIndex === -1 ? value.length : nextNewlineIndex;
  const lineText = value.slice(lineStart, lineEnd);

  return {
    lineStart,
    lineEnd,
    lineText,
    isCursorAtLineStart: cursor === lineStart,
    isCursorAtLineEnd: cursor === lineEnd,
  };
};

/**
 * Handle "-" + Space at the start of a line → visual bullet ("• ").
 */
export const getBulletSpaceTransform = (
  value: string,
  selectionStart: number,
  selectionEnd: number,
): BulletTransformResult | null => {
  const { lineStart, lineText, isCursorAtLineStart } = getCurrentLineInfo(value, selectionStart);

  // At start of a new line, user types "-" then presses Space:
  // "- " -> "• "
  const isHyphenAtLineStart = lineText === '-' && selectionStart === lineStart + 1;

  if (!isCursorAtLineStart && isHyphenAtLineStart) {
    const nextValue = value.slice(0, lineStart) + '• ' + value.slice(selectionEnd);
    const nextCursorPos = lineStart + 2; // after "• "
    return { nextValue, nextCursorPos };
  }

  return null;
};

/**
 * Handle Enter behavior inside bullet lists:
 * - create a new bullet when pressing Enter at the end of a bullet line with text
 * - exit the list when pressing Enter on an empty bullet line.
 */
export const getBulletEnterTransform = (
  value: string,
  selectionStart: number,
  selectionEnd: number,
): BulletTransformResult | null => {
  const { lineStart, lineEnd, lineText, isCursorAtLineEnd } = getCurrentLineInfo(value, selectionStart);
  const isBulletLine = lineText.startsWith('• ');

  if (!isBulletLine) {
    return null;
  }

  // Exit bullet list from an empty bullet after a previous bullet line.
  if (lineText.trim() === '•' && isCursorAtLineEnd) {
    // If this bullet is the first line
    if (lineStart === 0) {
      const nextValue = '\n' + value.slice(lineEnd);
      const nextCursorPos = 1;
      return { nextValue, nextCursorPos };
    }

    // For bullets that follow another line, remove the empty bullet
    // and move cursor to a new left-aligned line.
    const beforeBulletLine = value.slice(0, lineStart - 1); // up to (but excluding) the newline before this line
    const afterBulletLine = value.slice(lineEnd);
    const nextValue = beforeBulletLine + '\n\n' + afterBulletLine;
    const nextCursorPos = beforeBulletLine.length + 2; // after the two newlines
    return { nextValue, nextCursorPos };
  }

  // New bullet on Enter from the end of a bullet line with text
  if (isCursorAtLineEnd) {
    const insertion = '\n• ';
    const nextValue = value.slice(0, selectionStart) + insertion + value.slice(selectionEnd);
    const nextCursorPos = selectionStart + insertion.length;
    return { nextValue, nextCursorPos };
  }

  return null;
};

/**
 * Handle Backspace at the start of a bullet:
 * remove the bullet marker and reset alignment.
 */
export const getBulletBackspaceTransform = (value: string, selectionStart: number): BulletTransformResult | null => {
  const { lineStart, lineText, isCursorAtLineStart } = getCurrentLineInfo(value, selectionStart);
  const isBulletLine = lineText.startsWith('• ');

  if (isBulletLine && !isCursorAtLineStart && selectionStart === lineStart + 2) {
    const nextValue = value.slice(0, lineStart) + value.slice(lineStart + 2);
    const nextCursorPos = lineStart;
    return { nextValue, nextCursorPos };
  }

  return null;
};

/**
 * Normalize visual bullet characters (• ) into markdown bullets (- ) line-by-line.
 * This keeps the backend payload in markdown while allowing nicer bullets in the UI.
 */
export const normalizeMarkdownBullets = (text: string): string => {
  if (!text) return text;
  return text
    .split('\n')
    .map((line) => (line.startsWith('• ') ? `- ${line.slice(2)}` : line))
    .join('\n');
};

/** True when the browser URL already shows `/chat/:chatId` (skip redundant pending URL sync). */
export function isBrowserPathShowingChatId(chatId: string): boolean {
  if (typeof window === 'undefined') return false;
  const suffix = `/${AppRoutes.Chat}/${chatId}`;
  const { pathname } = window.location;
  return pathname === suffix || pathname.endsWith(suffix);
}
